import { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, Tooltip, useMapEvents } from 'react-leaflet'
import { toast } from 'sonner'
import { Shield, Trash2, Plus, AlertTriangle, PencilRuler } from 'lucide-react'
import {
    geofenceService, Geofence, GeofenceType, GeofenceEvent,
} from '@/services/geofenceService'

/** Captura clicks en el mapa para ir formando el polígono (modo dibujo). */
function DrawCapture({ active, onPoint }: { active: boolean; onPoint: (latlng: [number, number]) => void }) {
    useMapEvents({
        click: (e) => { if (active) onPoint([e.latlng.lat, e.latlng.lng]) },
    })
    return null
}

const TYPE_META: Record<GeofenceType, { label: string; color: string }> = {
    EXCLUSION: { label: 'Exclusión (no entrar)', color: '#ef4444' },
    POST: { label: 'Puesto (no abandonar)', color: '#f59e0b' },
    INCLUSION: { label: 'Inclusión (interés)', color: '#3b82f6' },
}

export default function GeofencesPage() {
    const [geofences, setGeofences] = useState<Geofence[]>([])
    const [breaches, setBreaches] = useState<GeofenceEvent[]>([])
    const [form, setForm] = useState({
        name: '', type: 'EXCLUSION' as GeofenceType,
        centerLat: -33.4489, centerLon: -70.6693, radiusMeters: 200,
    })
    const [saving, setSaving] = useState(false)
    const [drawMode, setDrawMode] = useState(false)
    const [vertices, setVertices] = useState<[number, number][]>([])

    const load = () => {
        geofenceService.list().then(setGeofences).catch(() => toast.error('No se pudieron cargar las geocercas'))
        geofenceService.events(true, 30).then(setBreaches).catch(() => {})
    }
    useEffect(load, [])

    const create = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!form.name.trim()) return toast.error('Indica un nombre')
        setSaving(true)
        try {
            await geofenceService.create(form)
            toast.success('Geocerca creada')
            setForm({ ...form, name: '' })
            load()
        } catch {
            toast.error('Error al crear la geocerca')
        } finally {
            setSaving(false)
        }
    }

    const finishPolygon = async () => {
        if (vertices.length < 3) return toast.error('Marca al menos 3 puntos en el mapa')
        if (!form.name.trim()) return toast.error('Indica un nombre')
        try {
            // Leaflet entrega [lat, lng]; el backend espera el anillo como [lon, lat].
            await geofenceService.create({
                name: form.name, type: form.type,
                polygon: vertices.map(([lat, lng]) => [lng, lat]),
            })
            toast.success('Geocerca (polígono) creada')
            setVertices([]); setDrawMode(false); setForm({ ...form, name: '' }); load()
        } catch {
            toast.error('Error al crear el polígono')
        }
    }

    const remove = async (id: number) => {
        if (!confirm('¿Eliminar esta geocerca?')) return
        try { await geofenceService.remove(id); toast.success('Eliminada'); load() }
        catch { toast.error('No se pudo eliminar') }
    }

    return (
        <div className="p-6 text-gray-100">
            <div className="flex items-center gap-3 mb-6">
                <Shield className="text-indigo-400" />
                <h1 className="text-2xl font-bold">Geocercas</h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Mapa */}
                <div className="lg:col-span-2 rounded-lg overflow-hidden border border-gray-700" style={{ height: 420 }}>
                    <MapContainer center={[form.centerLat, form.centerLon]} zoom={12} style={{ height: '100%', width: '100%', background: '#1a1a1a' }}>
                        <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                        {geofences.map((g) => (
                            <Polygon
                                key={g.id}
                                positions={g.polygon.map((p) => [p[1], p[0]] as [number, number])}
                                pathOptions={{ color: TYPE_META[g.type].color, fillOpacity: 0.15, weight: 2 }}
                            >
                                <Tooltip>{g.name} · {TYPE_META[g.type].label}</Tooltip>
                            </Polygon>
                        ))}
                        {/* Modo dibujo: captura de clicks + preview del polígono en curso */}
                        <DrawCapture active={drawMode} onPoint={(p) => setVertices((v) => [...v, p])} />
                        {vertices.length > 0 && (
                            <>
                                <Polyline positions={vertices} pathOptions={{ color: '#22d3ee', weight: 2 }} />
                                {vertices.map((v, i) => (
                                    <CircleMarker key={i} center={v} radius={4}
                                        pathOptions={{ color: '#22d3ee', fillColor: '#22d3ee', fillOpacity: 1 }} />
                                ))}
                            </>
                        )}
                    </MapContainer>
                </div>

                {/* Alta */}
                <form onSubmit={create} className="bg-gray-800/40 border border-gray-700 rounded-lg p-4 space-y-3">
                    <h2 className="font-semibold flex items-center gap-2"><Plus size={16} /> Nueva geocerca (círculo)</h2>
                    <input className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
                        placeholder="Nombre" value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <select className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
                        value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as GeofenceType })}>
                        {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <div className="grid grid-cols-2 gap-2">
                        <input type="number" step="any" className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
                            placeholder="Lat" value={form.centerLat}
                            onChange={(e) => setForm({ ...form, centerLat: parseFloat(e.target.value) })} />
                        <input type="number" step="any" className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
                            placeholder="Lon" value={form.centerLon}
                            onChange={(e) => setForm({ ...form, centerLon: parseFloat(e.target.value) })} />
                    </div>
                    <input type="number" className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
                        placeholder="Radio (m)" value={form.radiusMeters}
                        onChange={(e) => setForm({ ...form, radiusMeters: parseInt(e.target.value) })} />
                    <button disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-500 rounded px-3 py-2 text-sm font-medium disabled:opacity-50">
                        {saving ? 'Guardando…' : 'Crear (círculo)'}
                    </button>

                    {/* Dibujo de polígono sobre el mapa */}
                    <div className="pt-2 border-t border-gray-700 space-y-2">
                        {!drawMode ? (
                            <button type="button" onClick={() => { setDrawMode(true); setVertices([]) }}
                                className="w-full bg-cyan-700 hover:bg-cyan-600 rounded px-3 py-2 text-sm font-medium flex items-center justify-center gap-2">
                                <PencilRuler size={16} /> Dibujar polígono en el mapa
                            </button>
                        ) : (
                            <>
                                <p className="text-xs text-cyan-300">Haz clic en el mapa para marcar los vértices ({vertices.length}).</p>
                                <div className="flex gap-2">
                                    <button type="button" onClick={finishPolygon}
                                        className="flex-1 bg-emerald-600 hover:bg-emerald-500 rounded px-3 py-2 text-sm font-medium">Finalizar</button>
                                    <button type="button" onClick={() => { setDrawMode(false); setVertices([]) }}
                                        className="flex-1 bg-gray-700 hover:bg-gray-600 rounded px-3 py-2 text-sm font-medium">Cancelar</button>
                                </div>
                            </>
                        )}
                    </div>
                </form>
            </div>

            {/* Lista */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-gray-800/40 border border-gray-700 rounded-lg p-4">
                    <h2 className="font-semibold mb-3">Geocercas ({geofences.length})</h2>
                    <div className="space-y-2">
                        {geofences.map((g) => (
                            <div key={g.id} className="flex items-center justify-between bg-gray-900/50 rounded px-3 py-2 text-sm">
                                <span className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full" style={{ background: TYPE_META[g.type].color }} />
                                    {g.name} <span className="text-gray-500">· {TYPE_META[g.type].label}</span>
                                </span>
                                <button onClick={() => remove(g.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                            </div>
                        ))}
                        {geofences.length === 0 && <p className="text-gray-500 text-sm">Aún no hay geocercas.</p>}
                    </div>
                </div>

                {/* Breaches recientes */}
                <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-4">
                    <h2 className="font-semibold mb-3 flex items-center gap-2 text-amber-300"><AlertTriangle size={16} /> Violaciones recientes</h2>
                    <div className="space-y-2">
                        {breaches.map((b) => (
                            <div key={b.id} className="text-sm bg-gray-900/50 rounded px-3 py-2">
                                <span className="text-amber-300">{b.eventType}</span> · {b.callsign || b.userId}
                                <div className="text-gray-500 text-xs">{new Date(b.occurredAt).toLocaleString()}</div>
                            </div>
                        ))}
                        {breaches.length === 0 && <p className="text-gray-500 text-sm">Sin violaciones.</p>}
                    </div>
                </div>
            </div>
        </div>
    )
}
